export type ToastType = "info" | "success" | "error" | "warning";

/**
 * ⚠ **The hide timer is CLEARED, and its absence made a second toast inherit the first one's clock**
 * (owner report, 2026-08-25: *"the toast disappeared like 500ms after showing, so I never had a
 * chance to read what it said"*).
 *
 * There is ONE toast element, so a second call replaces the text of the first - but the first call's
 * `setTimeout` was still pending, and it hides the element on ITS schedule. So a surface that says
 * "Artist deleted" (2s) and then immediately shows a warning (8s) draws the warning for whatever is
 * left of the first two seconds and then hides it, with the second timer firing harmlessly into an
 * already-hidden node. **The longer, more important message is the one that gets cut off**, because
 * the short one is always the one that fired first.
 *
 * ⚠ It looks like a timing curiosity and it is a correctness bug about which sentence a person gets
 * to read - here it swallowed the only record of a page that may still have been live.
 */
let toastTimer: ReturnType<typeof setTimeout> | undefined;

export function toast(msg: string, duration = 3000, type: ToastType = "info") {
	const el = document.getElementById("toast")!;
	el.textContent = msg;
	el.className = "toast";
	if (type !== "info") el.classList.add(`toast-${type}`);
	el.classList.remove("hidden");
	clearTimeout(toastTimer);
	toastTimer = setTimeout(() => el.classList.add("hidden"), duration);
}

export { escapeHtml as escHtml } from "../../shared/html.ts";

/** Hostname of a URL, falling back to the raw string for malformed URLs (never throws) */
export function hostnameOf(url: string | null | undefined): string {
	if (!url) return "";
	try {
		return new URL(url).hostname;
	} catch {
		return url;
	}
}

/** Compact audience size: 980 · 14.7k · 1.2M (Spotify counts get long) */
export function formatFollowers(count: number): string {
	if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
	if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
	return String(count);
}

/**
 * Dates live with the other Intl helpers now (round 123) - `i18n.ts` owns every "let Intl do what
 * Intl owns" formatter, and a date's format is as per-locale as its month name. Re-exported here
 * because ~20 call sites import `formatDate` from this module and none of them should have to care.
 */
export { formatDate, formatDateRange } from "./i18n.ts";

/**
 * Put a button into a busy state - disabled, with a spinner and an optional
 * in-progress label - and return the function that restores it. Every action
 * that waits on an AI call (or any other slow request) should use this so the
 * user always sees that work is happening.
 *
 *   const done = setBusy(btn, "✨ Generating…");
 *   try { … } finally { done(); }
 */
export function setBusy(btn: HTMLButtonElement, label?: string): () => void {
	const originalText = btn.textContent ?? "";
	btn.disabled = true;
	btn.classList.add("btn-busy");
	if (label) btn.textContent = label;
	return () => {
		btn.disabled = false;
		btn.classList.remove("btn-busy");
		btn.textContent = originalText;
	};
}

export function showLoading(containerId: string) {
	const el = document.getElementById(containerId);
	if (el)
		el.innerHTML =
			'<div class="loading-skeleton"><div class="skeleton-line"></div><div class="skeleton-line short"></div><div class="skeleton-line"></div></div>';
}
